import React, { useState, useEffect } from 'react';
import { Tree } from 'react-arborist';
import { API_BASE_URL } from '../config/constants';
import {
  VscFile,
  VscJson,
  VscCode,
  VscMarkdown,
  VscFileMedia,
  VscFilePdf,
} from 'react-icons/vsc';
import {
  MdFolder,
  MdFolderOpen,
} from 'react-icons/md';
import {
  SiJavascript,
  SiTypescript,
  SiReact,
  SiPython,
  SiHtml5,
  SiCss3,
} from 'react-icons/si';

// Files and directories to ignore
const IGNORE_FOLDERS = ['__pycache__', '.venv', 'node_modules', '.git', 'codeflow', 'bonzai'];
const IGNORE_FILES = ['package.json', 'package-lock.json', '.gitignore', '.DS_Store'];

// Helper to check if a path is Claude-related
function isClaudeRelated(path) {
  if (!path) return false;
  // .claude folder and all its contents
  if (path.includes('.claude')) return true;
  // CLAUDE.md file
  if (path.endsWith('CLAUDE.md')) return true;
  // claude-usage.py or similar claude files
  if (path.includes('claude') && !path.includes('node_modules')) return true;
  return false;
}

function shouldIgnorePath(filePath) {
  const parts = filePath.split('/');
  const fileName = parts[parts.length - 1];
  const foldersToCheck = parts.slice(1);

  if (foldersToCheck.some(part => IGNORE_FOLDERS.includes(part))) {
    return true;
  }

  if (IGNORE_FILES.includes(fileName)) {
    return true;
  }

  return false;
}

function buildTreeData(files) {
  const tree = {};

  // Helper to check if a path part is a file (has extension)
  const isFilePath = (name) => {
    return name.includes('.') && !name.startsWith('.');
  };

  // Build the tree structure
  files.forEach(filepath => {
    if (!filepath || filepath.trim() === '') return;

    const parts = filepath.split('/');
    let current = tree;

    parts.forEach((part, index) => {
      if (!part || part.trim() === '') return;

      const isLastPart = index === parts.length - 1;
      const isFile = isLastPart && isFilePath(part);

      if (!current[part]) {
        const fullPath = parts.slice(0, index + 1).join('/');

        current[part] = {
          id: fullPath,
          name: part,
          path: fullPath,
          children: isFile ? undefined : {}
        };
      }

      if (current[part].children !== undefined) {
        current = current[part].children;
      }
    });
  });

  // Convert to array format with VSCode-style sorting (folders first, then files, both alphabetically)
  const convertToArray = (obj) => {
    const nodes = Object.values(obj).map(node => ({
      ...node,
      children: node.children !== undefined && Object.keys(node.children).length > 0
        ? convertToArray(node.children)
        : node.children !== undefined ? [] : undefined
    }));

    // Separate folders and files
    const folders = nodes.filter(node => node.children !== undefined);
    const files = nodes.filter(node => node.children === undefined);

    // Sort each group alphabetically (case-insensitive)
    const sortAlphabetically = (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    folders.sort(sortAlphabetically);
    files.sort(sortAlphabetically);

    // Return folders first, then files
    return [...folders, ...files];
  };

  return convertToArray(tree);
}

// Get file icon component based on extension
function getFileIcon(fileName, size = 16) {
  const ext = fileName.split('.').pop().toLowerCase();
  const style = { width: size, height: size, flexShrink: 0 };

  const iconMap = {
    js: <SiJavascript style={{ ...style, color: '#f7df1e' }} />,
    jsx: <SiReact style={{ ...style, color: '#61dafb' }} />,
    ts: <SiTypescript style={{ ...style, color: '#3178c6' }} />,
    tsx: <SiReact style={{ ...style, color: '#3178c6' }} />,
    json: <VscJson style={{ ...style, color: '#f7df1e' }} />,
    md: <VscMarkdown style={{ ...style, color: '#519aba' }} />,
    py: <SiPython style={{ ...style, color: '#3776ab' }} />,
    css: <SiCss3 style={{ ...style, color: '#264de4' }} />,
    html: <SiHtml5 style={{ ...style, color: '#e34c26' }} />,
    jpg: <VscFileMedia style={{ ...style, color: '#89ddff' }} />,
    jpeg: <VscFileMedia style={{ ...style, color: '#89ddff' }} />,
    png: <VscFileMedia style={{ ...style, color: '#89ddff' }} />,
    gif: <VscFileMedia style={{ ...style, color: '#89ddff' }} />,
    svg: <VscFileMedia style={{ ...style, color: '#ffb13b' }} />,
    pdf: <VscFilePdf style={{ ...style, color: '#ff0000' }} />,
  };

  return iconMap[ext] || <VscFile style={{ ...style, color: '#858585' }} />;
}

function Node({ node, style, dragHandle }) {
  const isFolder = node.data.children !== undefined;
  const [isHovered, setIsHovered] = useState(false);
  const [lineCount, setLineCount] = useState(null);
  const isClaude = isClaudeRelated(node.data.path);

  useEffect(() => {
    if (isFolder || !node.data.path) return;

    const fetchLineCount = async () => {
      try {
        const pathParts = node.data.path.split('/');
        const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : node.data.path;
        const response = await fetch(`${API_BASE_URL}/read?path=${encodeURIComponent(relativePath)}`, {
          method: 'GET',
          mode: 'cors'
        });
        if (!response.ok) return;

        const contentType = response.headers.get('content-type');
        let fileContent = '';
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          fileContent = data.content || data || '';
        } else {
          const text = await response.text();
          try {
            const data = JSON.parse(text);
            fileContent = data.content || data || '';
          } catch (e) {
            fileContent = text;
          }
        }
        setLineCount(fileContent.length === 0 ? 0 : fileContent.split('\n').length);
      } catch (err) {
        // silently fail
      }
    };

    fetchLineCount();
  }, [isFolder, node.data.path]);

  const textColor = node.state.isSelected
    ? '#1e1e1e'
    : isClaude
      ? '#f97316'
      : '#1e1e1e';

  return (
    <div
      ref={dragHandle}
      style={{
        ...style,
        paddingLeft: `${node.level * 16 + 4}px`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '1px 6px',
          fontSize: '13px',
          color: textColor,
          cursor: 'pointer',
          background: node.state.isSelected ? '#e3f2fd' : isHovered ? '#f5f5f5' : 'transparent',
          borderRadius: '4px',
          transition: 'background 0.1s',
          userSelect: 'none',
          height: '22px',
          lineHeight: '22px'
        }}
        onClick={() => {
          if (isFolder) {
            node.toggle();
          }
        }}
      >
        {/* Folder arrow or file icon */}
        {isFolder ? (
          <>
            <span style={{
              fontSize: '9px',
              color: '#666',
              width: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.1s',
              transform: node.isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
            }}>
              ▶
            </span>
            {node.isOpen ?
              <MdFolderOpen style={{ width: 16, height: 16, color: '#8ab4f8', flexShrink: 0 }} /> :
              <MdFolder style={{ width: 16, height: 16, color: '#8ab4f8', flexShrink: 0 }} />
            }
          </>
        ) : (
          <>
            <span style={{ width: '16px' }} />
            {getFileIcon(node.data.name, 16)}
          </>
        )}

        {/* File/folder name */}
        <span style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: '400',
          fontSize: '13px'
        }}>
          {node.data.name}
        </span>

        {/* Line count */}
        {!isFolder && lineCount !== null && (
          <span style={{
            fontSize: '11px',
            color: '#9ca3af',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            marginLeft: '6px',
            flexShrink: 0,
          }}>
            {lineCount}
          </span>
        )}
      </div>
    </div>
  );
}

function NewFileTree({ onFileClick }) {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [height, setHeight] = useState(800);
  const containerRef = React.useRef(null);

  // Update height when container resizes
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setHeight(rect.height || 800);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    // Also update on mount with a slight delay to ensure layout is settled
    const timer = setTimeout(updateHeight, 100);

    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/list`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        let fileArray = [];

        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          fileArray = data.files || data || [];
        } else {
          const text = await response.text();
          try {
            const data = JSON.parse(text);
            fileArray = data.files || data || [];
          } catch (e) {
            fileArray = text.trim().split('\n').filter(line => line.trim());
          }
        }

        const filteredFiles = fileArray.filter(filePath => !shouldIgnorePath(filePath));
        const tree = buildTreeData(filteredFiles);
        setTreeData(tree);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error fetching file list:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchFiles();
    const interval = setInterval(fetchFiles, 5000);
    return () => clearInterval(interval);
  }, []);

  // Build initial open state for all nodes
  const getInitialOpenState = React.useMemo(() => {
    const openState = {};

    const markOpen = (nodes) => {
      nodes.forEach(node => {
        // Always open root nodes or Claude-related nodes
        if (node.name === 'bonzai' || isClaudeRelated(node.path)) {
          openState[node.id] = true;
        }
        // Recursively check children
        if (node.children && node.children.length > 0) {
          markOpen(node.children);
        }
      });
    };

    markOpen(treeData);
    return openState;
  }, [treeData]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: '#6b7280',
        fontSize: '13px'
      }}>
        Loading files...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '16px',
        color: '#ef4444',
        fontSize: '13px'
      }}>
        Error loading files: {error}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ height: '100%', width: '100%', overflow: 'hidden', background: '#ffffff' }}>
      {height > 0 && (
        <Tree
          data={treeData}
          openByDefault={false}
          initialOpenState={getInitialOpenState}
          width="100%"
          height={height}
          indent={12}
          rowHeight={26}
          overscanCount={10}
          paddingTop={8}
          paddingBottom={40}
          onSelect={(nodes) => {
            if (nodes.length > 0 && !nodes[0].data.children && onFileClick) {
              onFileClick({ path: nodes[0].data.path, name: nodes[0].data.name });
            }
          }}
        >
          {Node}
        </Tree>
      )}
    </div>
  );
}

export default NewFileTree;
